import React, {useState, useEffect, useCallback, useRef} from 'react';
import {withTranslation} from '../../ChildrenContext.js';
import PropTypes from 'prop-types';
import CustomModal from '../../Modal';
import template from '../../../helpers/template';
import CustomNotify from "../../Notify";
import ClipboardCopy from '../../../helpers/ClipboardCopy';
import config, {statuses, metaUtmParams, allowGeneratePromocodes} from '../../../config';
import Button from "../../Button";
import Collapse, {Panel} from 'rc-collapse';
import Icon from '../../Icon';
import CustomTooltip from '../../Tooltip';
import FormGroup from '../../FormGroup';
import { useTemplateConfig } from '../../../context/TemplateConfigContext';

const creativeType = 'promocode';

const PromoCodeConstructor = ({
                                  offer: propOffer,
                                  goalEntryId: propGoalEntryId,
                                  linkId: propLinkId,
                                  visible: propVisible,
                                  access: propAccess,
                                  type: propType,
                                  creativeType: propCreativeType,
                                  creativeId: propCreativeId,
                                  promocode: propPromocode,
                                  goalEntries: propGoalEntries,
                                  publisherId: propPublisherId,
                                  publisherIsReferral: propPublisherIsReferral,
                                  codeParams: propCodeParams,
                                  promoTitle: propPromoTitle,
                                  description: propDescription,
                                  errors: propErrors,
                                  getDataLink: propGetDataLink,
                                  onClose: propOnClose,
                                  onSave: propOnSave,
                                  onValidate: propOnValidate,
                                  onEdit: propOnEdit,
                                  t
                              }) => {
    const [state, setState] = useState({
        creativeType: propCreativeType || creativeType,
        creativeId: propCreativeId || propLinkId,
        linkId: propLinkId,
        promocode: propPromocode,
        modalIsVisible: propVisible,
        goalEntries: propGoalEntries,
        publisherId: propPublisherId,
        publisherIsReferral: propPublisherIsReferral,
        link: {},
        metaParams: {},
        selfieLink: '',
        currentLinkId: '',
        resultLink: '',
        type: propType,
        codeParams: propCodeParams || {},
        promoTitle: propPromocode && propPromocode.title ? propPromocode.title : "",
        promoError: "",
        promoSuccess: "",
        description: propPromocode && propPromocode.description ? propPromocode.description : "",
        promoGoalEntryId: propPromocode && propPromocode.goalEntryId ? propPromocode.goalEntryId : null,
        errors: propErrors || '',
        goalEntryId: propGoalEntryId || null,
        access: !!propGoalEntryId && propAccess,
        loading: false,
        confirmDisabled: false
    });

    const { features } = useTemplateConfig();
    const staticNames = t?.constructors || {};
    const modalOptions = {
        promocode: {
            title: staticNames.createPromocode,
            pattern: '{{ promocode }}'
        },
        container: {
            title: staticNames.createContainer,
            pattern: '<iframe src="{{ promocode }}" width="{{ width }}" height="{{ height }}" align="middle">{{ text }}</iframe>'
        }
    };

    const getFirstGoal = useCallback(() => {
        let goal = state.goalEntries ? (state.goalEntries.find(g => g.access_granted) || {}) : {};
        return goal.id;
    }, [state.goalEntries]);

    const getMetaParams = useCallback(() => {
        let metaParams = Object.keys(state.metaParams).map((paramKey) => {
            let value = state.metaParams[paramKey];
            return value ? paramKey + '=' + value : ''
        });

        metaParams = metaParams.filter(n => {
            return n && n !== '&'
        });
        return metaParams.length ? '&' + metaParams.join('&') : '';
    }, [state.metaParams]);

    const getLinkParams = useCallback(() => {
        let linkIdParam = '?l=' + state.linkId,
            selfieLink = state.selfieLink;

        return selfieLink ? linkIdParam + '&sl=' + encodeURIComponent(selfieLink) : linkIdParam;
    }, [state.linkId, state.selfieLink]);

    const getResultLink = useCallback((promo, publisherIsReferral = false) => {
        let metaParams = getMetaParams();

        if (state.creativeType === 'promocode' && state.promocode && state.promocode.settings) {
            metaParams = '';
            let promoParams = typeof state.promocode.settings === "string" ? JSON.parse(state.promocode.settings) : state.promocode.settings;

            if (promoParams !== undefined && promoParams !== null) {
                Object.keys(promoParams).forEach((key) => {
                    if (promoParams[key] && promoParams[key].length > 0) {
                        metaParams = metaParams + '&' + key + '=' + promoParams[key];
                    }
                });
            }
        } else if (promo && promo.params) {
            metaParams = '';
            let promoParams = typeof promo.params === "string" ? JSON.parse(promo.params) : promo.params;

            if (promoParams !== undefined && promoParams !== null) {
                Object.keys(promoParams).forEach((key) => {
                    if (promoParams[key] && promoParams[key].length > 0) {
                        metaParams = metaParams + '&' + key + '=' + promoParams[key];
                    }
                });
            }
        }

        let advertiserIsReferral = false,
            offerIsReferral = false,
            currentGoalEntryId = state.goalEntryId;
        if (currentGoalEntryId && state.goalEntries.length) {
            state.goalEntries.forEach(function (goalEntry) {
                if (goalEntry.id === currentGoalEntryId) {
                    advertiserIsReferral = goalEntry.offer.advertiser.referral_advertiser;
                    offerIsReferral = goalEntry.offer.referral_offer;
                }
            });
        }

        metaParams = metaParams + '&creative_type=' + state.creativeType + '&creative_id=' + state.creativeId;

        if (publisherIsReferral && advertiserIsReferral && offerIsReferral) {
            metaParams += '&param8=referer_id';
        }

        return `${propOffer.tds_domain}/${state.publisherId}/${state.goalEntryId}${getLinkParams()}${metaParams}`;
    }, [state, getMetaParams, getLinkParams, propOffer]);

    const getCode = useCallback(() => {
        if (!modalOptions[state.type]) {
            return '';
        }

        let code = modalOptions[state.type].pattern;
        let promoObj = state.creativeType === 'promocode' && state.promocode ? state.promocode : state.link;
        const resolvedLink = getResultLink(promoObj, state.publisherIsReferral);
        let params = {
            ...state.codeParams,
            link: resolvedLink,
            promocode: resolvedLink
        };

        return template(code, params);
    }, [state.type, state.codeParams, state.link, state.promocode, state.creativeType, state.publisherIsReferral, getResultLink, modalOptions]);

    const getAllowChar = useCallback(() => {
        return /[^\da-zA-Z.%_{}\[\]#$\-]/g;
    }, []);

    const onErrorMsg = useCallback((key) => {
        CustomNotify((t && t.constructors && t.constructors[key]) || '', 'error');
    }, [t]);

    const successLink = useCallback((data) => {
        setState(prevState => ({
            ...prevState,
            link: data
        }));
    }, []);

    const initLink = useCallback(() => {
        if (!propLinkId) {
            return;
        }
        propGetDataLink(propLinkId, successLink);
    }, [propLinkId, propGetDataLink, successLink]);

    const setParam = useCallback((e) => {
        if (e.target.value.replace(getAllowChar(), '') !== e.target.value) {
            onErrorMsg('notAllowCharMsg');
            return;
        }

        const placeholder = e.target.placeholder || '';
        const inputValue = e.target.value || '';

        setState(prevState => {
            const newMetaParams = { ...prevState.metaParams };
            newMetaParams[placeholder] = inputValue;

            const newPromoCode = { ...prevState.promocode } || {};

            if (!newPromoCode.settings) {
                newPromoCode.settings = {};
            } else if (typeof newPromoCode.settings === "string") {
                newPromoCode.settings = JSON.parse(newPromoCode.settings);
            }

            newPromoCode.settings[placeholder] = inputValue;

            return {
                ...prevState,
                metaParams: newMetaParams,
                promocode: newPromoCode
            };
        });
    }, [getAllowChar, onErrorMsg]);

    const generatePromoCode = useCallback((e) => {
        let code = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ23456789';
        const charactersLength = 10;
        for (var i = 0; i < charactersLength; i++) {
            var randomPoz = Math.floor(Math.random() * characters.length);
            code += characters.substring(randomPoz, randomPoz + 1);
        }
        code = (state.publisherId + code).substr(0, 10);

        let error = code.length ? '' : ((t && t.constructors && t.constructors.promoCodeError) || '');

        setState(prevState => ({
            ...prevState,
            promoTitle: code,
            errors: error
        }));
    }, [state.publisherId]);

    const setPromoCode = useCallback((e) => {
        let error = e.target.value.length ? '' : ((t && t.constructors && t.constructors.promoCodeError) || '');

        setState(prevState => ({
            ...prevState,
            promoTitle: e.target.value.toUpperCase(),
            errors: error
        }));
    }, []);

    const setDescription = useCallback((e) => {
        setState(prevState => ({
            ...prevState,
            description: e.target.value
        }));
    }, []);

    const resultRef = useRef(null);
    const prevVisibleRef = useRef(false);
    const referralBadges = (t && t.referral) || {};

    const renderBadge = useCallback(() => {
        let advertiserIsReferral = false,
            offerIsReferral = false,
            currentGoalEntryId = state.goalEntryId;

        if (currentGoalEntryId && state.goalEntries.length) {
            state.goalEntries.forEach(function (goalEntry) {
                if (goalEntry.id === currentGoalEntryId) {
                    advertiserIsReferral = goalEntry.offer.advertiser.referral_advertiser;
                    offerIsReferral = goalEntry.offer.referral_offer;
                }
            });
        }

        if (state.publisherIsReferral && advertiserIsReferral && offerIsReferral) {
            return (
                <span className="badge badge-danger ml10" title={referralBadges.link}>RL</span>
            )
        }
        return null;
    }, [state.publisherIsReferral, state.goalEntryId, state.goalEntries, referralBadges]);

    const onCopyButtonClick = useCallback(() => {
        const code = getCode();
        if (!code || !code.trim()) {
            CustomNotify(staticNames.cantCreateUrlForCreativeNoLinksAvailable || 'No link to copy', 'error');
            return;
        }

        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(code).then(() => {
                CustomNotify(staticNames.copy || 'Copied');
            }).catch(() => {
                try {
                    const tmp = document.createElement('textarea');
                    tmp.value = code;
                    document.body.appendChild(tmp);
                    tmp.select();
                    document.execCommand('copy');
                    document.body.removeChild(tmp);
                    CustomNotify(staticNames.copy || 'Copied');
                } catch (e) {
                    CustomNotify(staticNames.copyError || 'Copy failed', 'error');
                }
            });
        } else {
            try {
                const tmp = document.createElement('textarea');
                tmp.value = code;
                document.body.appendChild(tmp);
                tmp.select();
                document.execCommand('copy');
                document.body.removeChild(tmp);
                CustomNotify(staticNames.copy || 'Copied');
            } catch (e) {
                CustomNotify(staticNames.copyError || 'Copy failed', 'error');
            }
        }
    }, [getCode, staticNames]);

    const setSelfieLink = useCallback((e) => {
        setState(prevState => ({
            ...prevState,
            selfieLink: e.target.value
        }));
    }, []);

    const onGoalChange = useCallback((e) => {
        const newGoalEntryId = e.target.value;
        setState(prevState => ({
            ...prevState,
            goalEntryId: newGoalEntryId,
            promoGoalEntryId: null
        }));
    }, []);

    const close = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            metaParams: {},
            selfieLink: '',
            errors: '',
            promoTitle: '',
            description: '',
            promoGoalEntryId: null
        }));
        propOnClose();
    }, [propOnClose]);

    const confirm = useCallback(() => {
        if (state.loading || state.confirmDisabled) {
            return;
        }

        if (!state.promoTitle || !state.promoTitle.length) {
            setState(prevState => ({
                ...prevState,
                errors: (t && t.constructors && t.constructors.promoCodeError) || ''
            }));
            return;
        }

        let params = {
            goalEntryId: state.goalEntryId,
            publisherId: state.publisherId,
            linkId: state.linkId,
            params: JSON.stringify(state.metaParams),
            promocode: state.promoTitle,
            description: state.description
        };

        propOnSave(params).then((resp) => {
            if (resp.success) {
                CustomNotify(resp.success);
                setState(prevState => ({
                    ...prevState,
                    metaParams: {},
                    selfieLink: '',
                    errors: ''
                }));
                propOnClose();
                setTimeout(function () {
                    window.location.reload();
                }, 3000);
            }
        }).catch(error => {
            if (error.error) {
                CustomNotify(error.error, 'error');
            }
        });
    }, [state, propOnSave, propOnClose]);

    const validate = useCallback(() => {
        if (state.loading || state.confirmDisabled) {
            return;
        }

        if (!state.promoTitle || !state.promoTitle.length) {
            setState(prevState => ({
                ...prevState,
                errors: (t && t.constructors && t.constructors.promoCodeError) || ''
            }));
            return;
        }

        let params = {
            goalEntryId: state.goalEntryId,
            publisherId: state.publisherId,
            linkId: state.linkId,
            params: JSON.stringify(state.metaParams),
            promocode: state.promoTitle,
            description: state.description
        };

        propOnValidate(params).then((resp) => {
            if (resp.success) {
                setState(prevState => ({
                    ...prevState,
                    errors: '',
                    promoSuccess: resp.success
                }));
            }
        }).catch(error => {
            if (error.error) {
                setState(prevState => ({
                    ...prevState,
                    errors: error.error,
                    promoSuccess: ''
                }));
            }
        });
    }, [state, propOnValidate]);

    const edit = useCallback(() => {
        if (!state.promocode.title || !state.promocode.title.length) {
            setState(prevState => ({
                ...prevState,
                errors: (t && t.constructors && t.constructors.promoCodeError) || ''
            }));
            return;
        }

        let params = features.promoCode.buildEditParams(state);

        propOnEdit(params).then((resp) => {
            if (resp.success) {
                CustomNotify(resp.success);
                propOnClose();
            }
        }).catch(error => {
            if (error.error) {
                CustomNotify(error.error, 'error');
            }
        });
    }, [state, propOnEdit, features.promoCode]);

    const getStatusIcon = useCallback(() => {
        const dataGoal = state.goalEntries.filter((item) => item.id === state.goalEntryId)[0] || {};

        return dataGoal.status === statuses.paused
            ? (
                <CustomTooltip id={`icon-paused-constructor`}
                               tooltip={(t && t.offersPage && t.offersPage.offer && t.offersPage.offer.paused) || ''}>
                    <Icon name={`link-construrtor__label-icon fa fa-pause-circle`}/>
                </CustomTooltip>
            )
            : null;
    }, [state.goalEntries, state.goalEntryId, t]);

    const renderGoalEntries = useCallback(() => {
        let editPromoCode = false;

        if (state.promocode && state.promocode.id) {
            editPromoCode = true;
        }

        const goalEntries = state.goalEntries || [];
        const selectValue = editPromoCode && state.promocode.goalEntryId
            ? state.promocode.goalEntryId
            : (state.promoGoalEntryId ? state.promoGoalEntryId : (state.goalEntryId || ''));

        return (
            <div className="form-group">
                <label className="control-label">{(t && t.constructors && t.constructors.goal) || ''}</label>
                {getStatusIcon()}
                <select onChange={onGoalChange}
                        value={selectValue}
                        className="form-control"
                        disabled={editPromoCode}
                >
                    {
                        Array.isArray(goalEntries) && goalEntries.length > 0
                            ? goalEntries.map((goal, key) => {
                                return <option
                                    key={key}
                                    disabled={!goal.access_granted}
                                    value={goal.id}

                                >
                                    {goal.goal.title}
                                </option>
                            })
                            : <option value="">No goals available</option>
                    }
                </select>
            </div>
        );
    }, [t, getStatusIcon, onGoalChange, state.goalEntryId, state.promoGoalEntryId, state.goalEntries, state.promocode]);

    const renderParams = useCallback((promoType) => {
        if (!metaUtmParams || !Array.isArray(metaUtmParams) || metaUtmParams.length === 0) {
            return null;
        }

        return (
            <div className="form-group">
                <label className="control-label bold">{(t && t.constructors && t.constructors.utm) || ''}</label>
                <div className="form-row">
                    {metaUtmParams.map(key => {
                        let value = (state.metaParams && state.metaParams[key]) ? state.metaParams[key] : '';
                        return (
                            <input
                                type="text"
                                maxLength="50"
                                onChange={setParam}
                                placeholder={key}
                                className="form-control"
                                value={value || ''}
                                key={key}
                                readOnly={!config.allowGeneratePromocodes}
                            />
                        )
                    })}
                </div>
            </div>
        );
    }, [t, state.metaParams, setParam]);

    const renderSubMetaParams = useCallback((promoType) => {
        if (!config.metaParamsCount || config.metaParamsCount <= 0) {
            return null;
        }

        return (
            <div className="form-group">
                <label className="control-label bold">{(t && t.constructors && t.constructors.subaccount) || ''}</label>
                <div className="form-row">
                    {Array.from(Array(config.metaParamsCount).keys()).map((index) => {
                        let value = (state.metaParams && state.metaParams['param' + (index + 1)]) ? state.metaParams['param' + (index + 1)] : '';
                        return (
                            <input type="text" maxLength="50"
                                   onChange={setParam}
                                   placeholder={'param' + (index + 1)}
                                   className="form-control"
                                   value={value || ''}
                                   key={index}
                                   readOnly={!config.allowGeneratePromocodes}
                            />
                        )
                    })}
                </div>
            </div>
        )
    }, [t, state.metaParams, setParam]);

    const renderPromoCode = useCallback(() => {
        let editPromoCode = false;

        if (state.promocode && state.promocode.id) {
            editPromoCode = true;
        }

        return (
            <div className="form-group">
                <div className="form-row">
                    <div className="form-row__promocode">
                        <label
                            className="control-label">{(t && t.constructors && t.constructors.promocode) || ''}</label>
                        <input
                            type="text"
                            maxLength="50"
                            placeholder={(t && t.constructors && t.constructors.promocode) || 'Promo Code'}
                            className={(state.errors && state.errors.length ? 'is-invalid' : '')
                                + (state.promoSuccess && state.promoSuccess.length ? 'is-valid' : '')
                                + " form-control"
                            }
                            id="promocode"
                            value={state.promoTitle || ""}
                            onChange={setPromoCode}
                            onKeyUp={validate}
                            required={true}
                            readOnly={editPromoCode}
                        />
                        {(state.errors && state.errors.length) || (state.promoSuccess && state.promoSuccess.length)
                            ? <span
                                className={(state.errors && state.errors.length ? 'invalid' : '')
                                    + (state.promoSuccess && state.promoSuccess.length ? 'valid' : '')
                                    + "-feedback help-block"
                                }>{state.errors + state.promoSuccess}</span>
                            : null
                        }
                        {(() => {
                            if (config.allowGeneratePromocodes) {
                                return <Button
                                    type={'button'}
                                    extraClass={{'generate-btn': true}}
                                    text={(t && t.constructors && t.constructors.generate) || 'Generate'}
                                    onClick={generatePromoCode}
                                    disabled={editPromoCode}
                                />
                            }
                        })()}
                    </div>
                </div>
            </div>
        );
    }, [t, state.promoTitle, state.errors, state.promocode, setPromoCode, validate, generatePromoCode]);

    const renderPromoCodeDescription = useCallback(() => {
        let editPromoCode = false;

        if (state.promocode && state.promocode.id) {
            editPromoCode = true;
        }

        const isDescriptionReadOnly = editPromoCode && !config.allowGeneratePromocodes;

        return (
            <div className="form-group">
                <label className="control-label">{(t && t.constructors && t.constructors.description) || ''}</label>
                <textarea
                    className="form-control"
                    maxLength="255"
                    name="description"
                    onChange={setDescription}
                    value={state.description || ""}
                    readOnly={isDescriptionReadOnly}
                    style={{background: isDescriptionReadOnly ? '#F6F6F6' : '#ffffff'}}
                />
            </div>
        );
    }, [t, state.description, state.promocode, setDescription]);

    const renderResult = useCallback(() => {
        const code = getCode();
        if (!code || !code.trim()) {
            return null;
        }

        return (
            <div className="form-group result-link">
                <label className="control-label bold">
                    {staticNames.result || 'Generated link'}
                    <a className="control-item m-l-xs"
                       onClick={onCopyButtonClick}
                       title={staticNames.copy || 'Copy'}
                       style={{ cursor: 'pointer' }}
                    >
                        <span className="fa fa-copy" />
                    </a>
                </label>
                <textarea
                    className="form-control"
                    value={code}
                    readOnly={true}
                    rows={2}
                    style={{ resize: 'none', background: '#F6F6F6' }}
                />
            </div>
        );
    }, [getCode, onCopyButtonClick, staticNames]);

    useEffect(() => {
        initLink();
    }, [initLink]);

    useEffect(() => {
        const wasVisible = prevVisibleRef.current;
        prevVisibleRef.current = propVisible;
        if (propVisible && !wasVisible) {
            if (propPromocode && propPromocode.settings) {
                try {
                    const promoParams = typeof propPromocode.settings === 'string' ? JSON.parse(propPromocode.settings) : propPromocode.settings;
                    setState(prev => ({ ...prev, metaParams: { ...(promoParams || {}) } }));
                } catch (e) {
                    setState(prev => ({ ...prev, metaParams: {} }));
                }
            } else {
                setState(prev => ({
                    ...prev,
                    metaParams: {},
                    selfieLink: '',
                    errors: '',
                    promoTitle: '',
                    description: '',
                    promoGoalEntryId: null
                }));
            }
        }
    }, [propVisible, propPromocode]);

    useEffect(() => {
        const isChangeLinkId = state.linkId !== propLinkId;
        let promoTitle = propPromocode ? propPromocode.title : "";
        let description = propPromocode ? propPromocode.description : "";
        let promoGoalEntryId = propPromocode ? propPromocode.goalEntryId : null;
        const resolvedPromoTitle = propPromoTitle || promoTitle;
        const resolvedDescription = propDescription || description;

        setState(prevState => {
            const prevPromoId = prevState.promocode && prevState.promocode.id ? prevState.promocode.id : null;
            const nextPromoId = propPromocode && propPromocode.id ? propPromocode.id : null;
            const modalJustOpened = propVisible && !prevState.modalIsVisible;
            const promoChanged = prevPromoId !== nextPromoId;
            const shouldSyncEditableFields = modalJustOpened || promoChanged || (features.promoCode.shouldSyncWhenHidden && !propVisible);

            let nextState = {
                ...prevState,
                creativeType: propCreativeType || creativeType,
                creativeId: propCreativeId || propLinkId,
                goalEntries: propGoalEntries,
                publisherId: propPublisherId,
                linkId: propLinkId,
                promocode: propPromocode,
                type: propType,
                selfieLink: '',
                codeParams: propCodeParams || {},
                access: !!getFirstGoal() && propAccess,
                modalIsVisible: propVisible,
                errors: propErrors,
                goalEntryId: propGoalEntryId || getFirstGoal()
            };

            if (shouldSyncEditableFields) {
                nextState.promoTitle = resolvedPromoTitle;
                nextState.description = resolvedDescription;
                nextState.promoGoalEntryId = propGoalEntryId || promoGoalEntryId;
            }

            if (shouldSyncEditableFields && propPromocode && propPromocode.settings) {
                let promoParams = typeof propPromocode.settings === "string" ? JSON.parse(propPromocode.settings) : propPromocode.settings;
                if (promoParams !== undefined && promoParams !== null) {
                    nextState.metaParams = { ...promoParams };
                }
            }

            return nextState;
        });

        if (isChangeLinkId) {
            initLink();
        }
    }, [propCreativeType, propCreativeId, propLinkId, propGoalEntries, propPublisherId, propPromocode, propType, propCodeParams, propAccess, propVisible, propPromoTitle, propDescription, propErrors, propGoalEntryId, getFirstGoal, initLink, state.linkId]);

    if (undefined === modalOptions[state.type]) {
        return '';
    }

    if ("object" === typeof state.link && 0 === Object.keys(state.link).length && state.modalIsVisible) {
        return '';
    }
    if (undefined === state.link && state.modalIsVisible) {
        CustomNotify(staticNames.cantCreateUrlForCreativeNoLinksAvailable, 'error');
        return '';
    }

    let options = modalOptions[state.type];
    let editPromoCode = false;

    if (state.promocode && state.promocode.id) {
        editPromoCode = true;
    }

    return (
        <div className="promocode-construrtor">
            {!state.access
                ? <CustomModal
                    visible={state.modalIsVisible}
                    onClose={propOnClose}
                    onConfirm={propOnClose}
                    confirmName={staticNames.confirm}
                    title={editPromoCode ? (config.allowGeneratePromocodes ? staticNames.editPromocode : staticNames.infoPromocode) : options.title}
                    areNoControls={true}
                    disableHistoryTracking={true}
                >
                    <div className="row row-line">
                        <div className="col-sm-12">
                            <span>{staticNames.getAccess}.</span>
                        </div>
                    </div>
                </CustomModal>
                : <CustomModal
                    visible={state.modalIsVisible}
                    onClose={propOnClose}
                    onConfirm={editPromoCode ? edit : confirm}
                    confirmName={staticNames.save}
                    title={editPromoCode ? (config.allowGeneratePromocodes ? staticNames.editPromocode : staticNames.infoPromocode) : options.title}
                    areNoControls={editPromoCode ? !config.allowGeneratePromocodes : false}
                    disableHistoryTracking={true}
                >
                    {renderGoalEntries()}

                    {renderParams(creativeType)}

                    {renderSubMetaParams(creativeType)}

                    {renderPromoCode()}

                    {renderPromoCodeDescription()}

                    {renderResult()}

                </CustomModal>
            }
        </div>
    );
};

PromoCodeConstructor.propTypes = {
    offer: PropTypes.object.isRequired,
    goalEntryId: PropTypes.string,
    linkId: PropTypes.string,
    visible: PropTypes.bool,
    access: PropTypes.bool,
    type: PropTypes.string,
    creativeType: PropTypes.string,
    creativeId: PropTypes.string,
    promocode: PropTypes.object,
    goalEntries: PropTypes.array,
    publisherId: PropTypes.string,
    publisherIsReferral: PropTypes.bool,
    codeParams: PropTypes.object,
    promoTitle: PropTypes.string,
    description: PropTypes.string,
    errors: PropTypes.string,
    getDataLink: PropTypes.func,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    onValidate: PropTypes.func,
    onEdit: PropTypes.func,
    t: PropTypes.object
};

PromoCodeConstructor.defaultProps = {
    type: creativeType,
    creativeType: creativeType,
    creativeId: '',
    promocode: {},
    goalEntries: [],
    publisherId: '',
    publisherIsReferral: false,
    codeParams: {},
    promoTitle: '',
    description: '',
    errors: '',
    getDataLink: () => {
    },
    onClose: () => {
    },
    onSave: () => {
    },
    onValidate: () => {
    },
    onEdit: () => {
    }
};

export default withTranslation(PromoCodeConstructor);
